import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
    Fab,
    Tabs, Tab,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    Divider, Grid, Chip, Avatar,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Card, CardContent,
    ListItemAvatar
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    ArrowBack as ArrowBackIcon,
    Description as DescriptionIcon,
    People as PeopleIcon,
    History as HistoryIcon,
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon,
    Launch as LaunchIcon,
    PersonRemove as PersonRemoveIcon,
    Image as ImageIcon,
    Assignment as AssignmentIcon,
    AttachFile as AttachFileIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    EditNote as EditNoteIcon,
    SwapHoriz as SwapHorizIcon,
    DeleteForever as DeleteForeverIcon,
    PersonAdd as PersonAddIcon,
    VisibilityOff as VisibilityOffIcon,
    Visibility as VisibilityIcon
} from "@mui/icons-material";
import { format, parseISO } from 'date-fns';

import MaintenanceForm from "./utils/MaintenanceForm";
import MaintenanceDocForm from "./utils/MaintenanceDocForm";
import AssignEmployeeForm from "./utils/MaintenanceAssignEmployeeForm";
import {
    useMaintenances,
    useMaintenanceById,
    useMaintenanceEmployees,
} from "../../hooks/maintenance/useMaintenances";

import { useAuthContext } from "../../context/AuthProvider";
import { useMaintenanceDocs } from "../../hooks/maintenance/useMaintenanceDocs";
import { useMaintenanceHistory } from "../../hooks/maintenance/useMaintenanceHistory";
import { useMaintenanceDetails } from "../../hooks/maintenance/useMaintenanceDetails";

const MaintenanceDetailsPage = () => {
    const { manId } = useParams();
    const navigate = useNavigate();

    const [currentTab, setCurrentTab] = useState(0);

    const [formState, setFormState] = useState({ open: false, maintenance: null });
    const [docFormState, setDocFormState] = useState({ open: false, doc: null });
    const [assignEmpFormState, setAssignEmpFormState] = useState({ open: false });

    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [confirmDeleteMaintenanceDialog, setConfirmDeleteMaintenanceDialog] = useState({ open: false, maintenance: null });
    const [confirmDeleteDocDialog, setConfirmDeleteDocDialog] = useState({ open: false, doc: null });
    const [confirmRemoveEmployeeDialog, setConfirmRemoveEmployeeDialog] = useState({ open: false, employeeAssignment: null });

    const [openPreview, setOpenPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    const { user } = useAuthContext();

    const {
        data: maintenanceDetails,
        isLoading: isLoadingMaintenanceDetail,
        error: errorMaintenanceDetail,
    } = useMaintenanceDetails(parseInt(manId, 10));

    const {
        updateMutation: updateMaintenanceMutation,
        deleteMutation: deleteMaintenanceMutation,
    } = useMaintenances(); // Sin comId, porque estas mutaciones no dependen de un filtro de componente.

    // 3. Hooks para las SUB-ENTIDADES, pasando el manId del mantenimiento actual.
    const {
        maintenanceDocs,
        isLoading: isLoadingDocs,
        error: errorDocs,
        createMutation: createDocMutation,
        updateMutation: updateDocMutation,
        deleteMutation: deleteDocMutation,
    } = useMaintenanceDocs(parseInt(manId, 10));

    const {
        assignedEmployees,
        isLoading: isLoadingAssignedEmployees,
        error: errorAssignedEmployees,
        assignEmployeeMutation,
        removeEmployeeMutation,
    } = useMaintenanceEmployees(parseInt(manId, 10)); // <-- ¡Uso correcto!

    const {
        maintenanceHistory,
        isLoading: isLoadingHistory,
        error: errorHistory,
    } = useMaintenanceHistory(parseInt(manId, 10));


    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleOpenMaintenanceForm = (maintenance = null) => {
        setFormState({ open: true, maintenance });
    };

    const handleCloseMaintenanceForm = () => {
        setFormState({ open: false, maintenance: null });
    };

    const handleSubmitMaintenance = useCallback((data) => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión para realizar esta acción.", "error");
            return;
        }

        const payload = { ...data };
        payload.updbyid = user.EmpID;

        // updateMaintenanceMutation se obtiene de useMaintenances() sin parámetros
        updateMaintenanceMutation.mutate(
            { id: maintenanceDetails.manid, data: payload },
            {
                onSuccess: () => {
                    showAlert("Mantenimiento actualizado correctamente");
                    handleCloseMaintenanceForm();
                },
                onError: (mutationError) => {
                    console.error("Error al actualizar mantenimiento:", mutationError);
                    showAlert(mutationError.message || "Error al actualizar", "error");
                },
            }
        );
    }, [maintenanceDetails, updateMaintenanceMutation, user]);


    const handleConfirmDeleteMaintenance = () => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión.", "error");
            setConfirmDeleteMaintenanceDialog({ open: false, maintenance: null });
            return;
        }
        // deleteMaintenanceMutation se obtiene de useMaintenances() sin parámetros
        deleteMaintenanceMutation.mutate(
            { id: confirmDeleteMaintenanceDialog.maintenance.manid, updbyid: user.EmpID },
            {
                onSuccess: () => {
                    showAlert("Mantenimiento eliminado lógicamente.");
                    setConfirmDeleteMaintenanceDialog({ open: false, maintenance: null });
                    navigate(`/operaciones/componente/${maintenanceDetails.comid}/mantenimiento`);
                },
                onError: (mutationError) => {
                    console.error("Error al eliminar mantenimiento:", mutationError);
                    showAlert(mutationError.message || "Error al eliminar", "error");
                },
            }
        );
    };

    // --- Handlers para Documentos ---
    const handleOpenDocForm = (doc = null) => {
        setDocFormState({ open: true, doc });
    };

    const handleCloseDocForm = () => {
        setDocFormState({ open: false, doc: null });
    };

    const handleSubmitDoc = useCallback((formDataFromChild) => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión para realizar esta acción.", "error");
            return;
        }

        // `formDataFromChild` ahora es `{ docnom, tipdoc, tipcomid, selectedFile }`
        const dataFromForm = formDataFromChild; // Renombrar para claridad

        const formDataToSendToAPI = new FormData();

        // Añadir campos de texto al FormData
        formDataToSendToAPI.append("manid", maintenanceDetails.manid);
        formDataToSendToAPI.append("docnom", dataFromForm.docnom);
        formDataToSendToAPI.append("tipdoc", dataFromForm.tipdoc);
        if (dataFromForm.tipcomid) {
            formDataToSendToAPI.append("tipcomid", dataFromForm.tipcomid);
        } else if (dataFromForm.tipdoc === 'CONTABLE') {
            formDataToSendToAPI.append("tipcomid", '');
        }

        // Manejo del archivo
        if (dataFromForm.selectedFile) { // Usa el archivo que viene del formulario hijo
            formDataToSendToAPI.append("document", dataFromForm.selectedFile);
        } // Si no hay selectedFile, no se añade el campo 'document' al FormDataToSendToAPI, lo cual es correcto para edición sin cambio de archivo.

        if (docFormState.doc) { // Edición
            formDataToSendToAPI.append("updbyid", user.EmpID);
            updateDocMutation.mutate(
                { id: docFormState.doc.docid, formData: formDataToSendToAPI }, // Pasa el FormData aquí
                {
                    onSuccess: () => {
                        showAlert("Documento actualizado correctamente");
                        handleCloseDocForm();
                    },
                    onError: (mutationError) => {
                        console.error("Error al actualizar documento:", mutationError);
                        showAlert(mutationError.message || "Error al actualizar", "error");
                    },
                }
            );
        } else { // Creación
            // Asegúrate de que para la creación, haya un archivo si no lo valida en el formulario hijo
            if (!dataFromForm.selectedFile) {
                showAlert("Debe seleccionar un archivo para subir.", "error");
                return;
            }
            formDataToSendToAPI.append("crebyid", user.EmpID);
            createDocMutation.mutate(formDataToSendToAPI, {
                onSuccess: () => {
                    showAlert("Documento subido correctamente");
                    handleCloseDocForm();
                },
                onError: (mutationError) => {
                    console.error("Error al subir documento:", mutationError);
                    showAlert(mutationError.message || "Error al subir", "error");
                },
            });
        }
    }, [docFormState.doc, createDocMutation, updateDocMutation, user, maintenanceDetails]);

    const handleConfirmDeleteDoc = () => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión.", "error");
            setConfirmDeleteDocDialog({ open: false, doc: null });
            return;
        }
        deleteDocMutation.mutate(
            { id: confirmDeleteDocDialog.doc.docid, updbyid: user.EmpID },
            {
                onSuccess: () => {
                    showAlert("Documento eliminado correctamente");
                    setConfirmDeleteDocDialog({ open: false, doc: null });
                },
                onError: (mutationError) => {
                    console.error("Error al eliminar documento:", mutationError);
                    showAlert(mutationError.message || "Error al eliminar", "error");
                },
            }
        );
    };

    const handleDownloadDoc = (docRuta) => {
        if (!docRuta) {
            showAlert("Ruta del documento no disponible para previsualizar.", "error");
            return;
        }
        const fullUrl = `${process.env.REACT_APP_API_URL}/documents/file/${docRuta}`;
        window.open(fullUrl, '_blank');
    };

    const handleOpenDocument = (docRuta) => {
        if (!docRuta) {
            showAlert("Ruta del documento no disponible para previsualizar.", "error");
            return;
        }
        const fullUrl = `${process.env.REACT_APP_API_URL}/documents/file/${docRuta}`;

        setPreviewUrl(fullUrl);
        setOpenPreview(true);
    };

    const handleClosePreview = () => {
        setOpenPreview(false);
        setPreviewUrl(null);
    };

    // --- Handlers para Empleados ---
    const handleOpenAssignEmployeeForm = () => {
        setAssignEmpFormState({ open: true });
    };

    const handleCloseAssignEmployeeForm = () => {
        setAssignEmpFormState({ open: false });
    };

    const handleSubmitAssignEmployee = useCallback((data) => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión.", "error");
            return;
        }
        assignEmployeeMutation.mutate(
            { manId: maintenanceDetails.manid, empId: data.empid, rolman: data.rolman, crebyid: user.EmpID },
            {
                onSuccess: () => {
                    showAlert("Empleado asignado correctamente");
                    handleCloseAssignEmployeeForm();
                },
                onError: (mutationError) => {
                    console.error("Error al asignar empleado:", mutationError);
                    showAlert(mutationError.message || "Error al asignar", "error");
                },
            }
        );
    }, [maintenanceDetails, assignEmployeeMutation, user]);

    const handleConfirmRemoveEmployee = () => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión.", "error");
            setConfirmRemoveEmployeeDialog({ open: false, employeeAssignment: null });
            return;
        }
        removeEmployeeMutation.mutate(
            { manId: confirmRemoveEmployeeDialog.employeeAssignment.manid, empId: confirmRemoveEmployeeDialog.employeeAssignment.empid, updbyid: user.EmpID },
            {
                onSuccess: () => {
                    showAlert("Empleado desasignado correctamente.");
                    setConfirmRemoveEmployeeDialog({ open: false, employeeAssignment: null });
                },
                onError: (mutationError) => {
                    console.error("Error al desasignar empleado:", mutationError);
                    showAlert(mutationError.message || "Error al desasignar", "error");
                },
            }
        );
    };

    // --- Utilidades ---
    const formatDateTime = (dateString) => {
        if (!dateString) return '—';
        try {
            return format(parseISO(dateString), 'dd/MM/yyyy HH:mm');
        } catch (e) {
            console.warn("Could not parse date:", dateString, e);
            return dateString;
        }
    };

    const getMaintenanceStatusChipColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'CREADO': return 'info';
            case 'INICIADO': return 'primary';
            case 'EN_PROCESO': return 'warning';
            case 'FINALIZADO': return 'success';
            case 'CANCELADO': return 'error';
            default: return 'default';
        }
    };

    // --- Estados de Carga y Error Globales ---
    // Usamos isLoadingMaintenanceDetail y errorMaintenanceDetail para el manejo del detalle
    if (isLoadingMaintenanceDetail) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Cargando detalles del mantenimiento...</Typography>
            </Paper>
        );
    }

    if (errorMaintenanceDetail && !maintenanceDetails) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="error" variant="h6">Error al cargar mantenimiento: {errorMaintenanceDetail.message}</Typography>
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>Volver</Button>
            </Paper>
        );
    }

    if (!maintenanceDetails) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="text.secondary" variant="h6">Mantenimiento no encontrado.</Typography>
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>Volver</Button>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={() => navigate(`/operaciones/componente/${maintenanceDetails.comid}/mantenimiento`)} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5">Detalles del Mantenimiento #{maintenanceDetails.manid}</Typography>
                </Box>
                {(maintenanceDetails.manest !== 'FINALIZADO') && (
                    <Box>
                        <Tooltip title="Editar Mantenimiento">
                            <IconButton onClick={() => handleOpenMaintenanceForm(maintenanceDetails)}>
                                <EditIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar Mantenimiento (Lógico)">
                            <IconButton onClick={() => setConfirmDeleteMaintenanceDialog({ open: true, maintenance: maintenanceDetails })}>
                                <DeleteIcon color="error" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Información General del Mantenimiento */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12}> {/* La tarjeta principal ocupa todo el ancho */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Información General del Mantenimiento</Typography>
                            <Divider sx={{ mb: 2 }} /> {/* Más espacio después del título */}

                            <Grid container spacing={2}> {/* Nuevo Grid para las dos columnas internas */}
                                <Grid item xs={12} sm={6}> {/* Primera columna: Detalles clave */}
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>ID Mantenimiento:</strong> {maintenanceDetails.manid}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Tipo:</strong> {maintenanceDetails.tipomantenimientonombre || 'N/A'}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Estado:</strong> <Chip label={maintenanceDetails.manest} color={getMaintenanceStatusChipColor(maintenanceDetails.manest)} size="small" sx={{ ml: 1 }} />
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Fecha Inicio:</strong> {formatDateTime(maintenanceDetails.manfecini)}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Fecha Fin:</strong> {formatDateTime(maintenanceDetails.manfecfin)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}> {/* Segunda columna: Detalles de Componente, Métricas y Observaciones */}
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Componente:</strong> {maintenanceDetails.componentenombre || 'N/A'} (ID: {maintenanceDetails.comid})
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Vehículo:</strong> {maintenanceDetails.vehiculomatricula || 'N/A'} - {maintenanceDetails.vehiculomodelo || 'N/A'}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Kilometraje:</strong> {maintenanceDetails.mankm ? `${maintenanceDetails.mankm} km` : 'N/A'}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Tiempo Fuera:</strong> {maintenanceDetails.mantiefuehor ? `${maintenanceDetails.mantiefuehor} horas` : 'N/A'}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Costo Total:</strong> {maintenanceDetails.costototal ? `$${parseFloat(maintenanceDetails.costototal).toFixed(2)}` : 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}> {/* Fila para Observaciones (ocupará todo el ancho) */}
                                    <Typography variant="body1">
                                        <strong>Observaciones:</strong> {maintenanceDetails.manobs || 'Sin observaciones.'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}> {/* Fila para Auditoría (ocupará todo el ancho) */}
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        Creado el: {formatDateTime(maintenanceDetails.credat)} por {maintenanceDetails.crebyid}
                                    </Typography>
                                    {maintenanceDetails.updbyid && maintenanceDetails.upddat && (
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Última actualización: {formatDateTime(maintenanceDetails.upddat)} por {maintenanceDetails.updbyid}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs para Sub-funcionalidades */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={currentTab} onChange={(event, newValue) => setCurrentTab(newValue)} aria-label="Maintenance Details Tabs">
                    <Tab label="Documentos" icon={<DescriptionIcon />} iconPosition="start" />
                    <Tab label="Empleados Asignados" icon={<PeopleIcon />} iconPosition="start" />
                    <Tab label="Historial de Eventos" icon={<HistoryIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {/* Contenido de las Tabs */}
            {/* Tab de Documentos */}
            {currentTab === 0 && (
                <Box sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Documentos del Mantenimiento</Typography>
                        {(maintenanceDetails.manest !== 'FINALIZADO') && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDocForm()}
                                color="success"
                            >
                                Añadir Documento
                            </Button>
                        )}
                    </Box>

                    {isLoadingDocs ? (
                        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                    ) : errorDocs ? (
                        <Typography color="error" sx={{ p: 2 }}>Error al cargar documentos: {errorDocs.message}</Typography>
                    ) : (
                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}> {/* Usar Paper para enmarcar la lista */}
                            <List disablePadding> {/* disablePadding para que la lista ocupe todo el Paper */}
                                {maintenanceDocs.length > 0 ? (
                                    maintenanceDocs.map((doc) => (
                                        <React.Fragment key={doc.docid}>
                                            <ListItem
                                                // Añadir un pequeño padding extra y hover effect para interactividad
                                                sx={{ py: 1.5, '&:hover': { backgroundColor: 'action.hover' } }}
                                            >
                                                {/* Icono de tipo de documento */}
                                                <ListItemAvatar sx={{ minWidth: 40 }}> {/* Ajustar el espacio del icono */}
                                                    {doc.tipdoc === 'CONTABLE' ? <DescriptionIcon color="info" /> :
                                                        doc.tipdoc === 'FOTO' ? <ImageIcon color="secondary" /> :
                                                            doc.tipdoc === 'REPORTE' ? <AssignmentIcon color="warning" /> :
                                                                <AttachFileIcon color="action" /> /* Ícono genérico */
                                                    }
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" alignItems="center" flexWrap="wrap">
                                                            <Typography component="span" variant="body1" fontWeight="bold" sx={{ mr: 1 }}>
                                                                {doc.docnom}
                                                            </Typography>
                                                            <Chip
                                                                label={doc.tipdoc}
                                                                size="small"
                                                                color={
                                                                    doc.tipdoc === 'CONTABLE' ? 'primary' :
                                                                        doc.tipdoc === 'FOTO' ? 'secondary' :
                                                                            doc.tipdoc === 'REPORTE' ? 'warning' : 'default'
                                                                }
                                                                sx={{ mr: 1 }}
                                                            />
                                                            {doc.tipdoc === 'CONTABLE' && doc.tipocomprobantenombre && (
                                                                <Typography component="span" variant="body2" color="text.secondary">
                                                                    ({doc.tipocomprobantenombre})
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                            Subido el: {formatDateTime(doc.credat)}
                                                        </Typography>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <Tooltip title="Ver Documento"> {/* Tooltip para ver */}
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="view"
                                                            onClick={() => handleOpenDocument(doc.docruta)} // Llama a la nueva función para previsualizar
                                                            color="info" // Puedes mantener el color que tenías o cambiarlo
                                                        >
                                                            <VisibilityIcon /> {/* Icono de visibilidad */}
                                                        </IconButton>
                                                    </Tooltip>
                                                    {/*<Tooltip title="Descargar Documento">
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="download"
                                                            onClick={() => {
                                                                const downloadUrl = `${process.env.REACT_APP_API_URL}/${doc.docruta}`;
                                                                window.open(downloadUrl, '_blank');
                                                            }}                                                            
                                                            color="secondary"
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Tooltip>*/}
                                                    {(maintenanceDetails.manest !== 'FINALIZADO') && (
                                                        <Tooltip title="Editar Documento">
                                                            <IconButton edge="end" aria-label="edit" onClick={() => handleOpenDocForm(doc)}>
                                                                <EditIcon color="primary" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(maintenanceDetails.manest !== 'FINALIZADO') && (
                                                        <Tooltip title="Eliminar Documento">
                                                            <IconButton edge="end" aria-label="delete" onClick={() => setConfirmDeleteDocDialog({ open: true, doc })}>
                                                                <DeleteIcon color="error" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            <Divider component="li" />
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        No hay documentos asociados a este mantenimiento.
                                    </Typography>
                                )}
                            </List>
                        </Paper>
                    )}
                </Box>
            )}

            {/* Tab de Empleados Asignados */}
            {currentTab === 1 && (
                <Box sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Empleados Asignados</Typography>
                        {(maintenanceDetails.manest !== 'FINALIZADO') && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenAssignEmployeeForm()}
                                color="success" // Botón de añadir en color verde
                            >
                                Asignar Empleado
                            </Button>
                        )}
                    </Box>
                    {isLoadingAssignedEmployees ? (
                        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                    ) : errorAssignedEmployees ? (
                        <Typography color="error">Error al cargar empleados asignados: {errorAssignedEmployees.message}</Typography>
                    ) : (
                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}> {/* Usar Paper para enmarcar la lista */}
                            <List disablePadding>
                                {assignedEmployees.length > 0 ? (
                                    assignedEmployees.map((emp) => (
                                        <React.Fragment key={emp.manempid}>
                                            <ListItem
                                                // Añadir un pequeño padding extra y hover effect para interactividad
                                                sx={{ py: 1.5, '&:hover': { backgroundColor: 'action.hover' } }}
                                            >
                                                {/* Avatar con iniciales del empleado */}
                                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                                    <Avatar>
                                                        {emp.personanombre ? emp.personanombre.charAt(0).toUpperCase() : ''}
                                                        {emp.personaapellido ? emp.personaapellido.charAt(0).toUpperCase() : ''}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography component="span" variant="body1" fontWeight="bold">
                                                            {`${emp.personanombre} ${emp.personaapellido || ''}`}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <React.Fragment>
                                                            <Typography component="span" variant="body2" color="text.primary" display="block">
                                                                Rol: <Chip label={emp.rolman || 'Sin rol'} size="small" variant="outlined" />
                                                            </Typography>
                                                        </React.Fragment>
                                                    }
                                                />
                                                {(maintenanceDetails.manest !== 'FINALIZADO') && (
                                                    <ListItemSecondaryAction>
                                                        <Tooltip title="Desasignar Empleado">
                                                            <IconButton edge="end" aria-label="remove" onClick={() => setConfirmRemoveEmployeeDialog({ open: true, employeeAssignment: emp })}>
                                                                <PersonRemoveIcon color="error" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </ListItemSecondaryAction>
                                                )}
                                            </ListItem>
                                            <Divider component="li" />
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        No hay empleados asignados a este mantenimiento.
                                    </Typography>
                                )}
                            </List>
                        </Paper>
                    )}
                </Box>
            )}

            {/* Tab de Historial de Eventos */}
            {currentTab === 2 && (
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" mb={2}>Historial de Eventos del Mantenimiento</Typography>
                    {isLoadingHistory ? (
                        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                    ) : errorHistory ? (
                        <Typography color="error">Error al cargar historial: {errorHistory.message}</Typography>
                    ) : (
                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}> {/* Usar Paper para enmarcar la lista */}
                            <List disablePadding>
                                {maintenanceHistory.length > 0 ? (
                                    maintenanceHistory.map((hist) => (
                                        <React.Fragment key={hist.hismanid}>
                                            <ListItem sx={{ py: 1.5 }}>
                                                {/* Icono de Evento (opcional, si quieres diferentes iconos por tipo de evento) */}
                                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                                    {hist.hismaneve === 'MANTENIMIENTO_CREADO' ? <AddCircleOutlineIcon color="success" /> :
                                                        hist.hismaneve === 'MANTENIMIENTO_ACTUALIZADO' ? <EditNoteIcon color="primary" /> :
                                                            hist.hismaneve === 'ESTADO_MANTENIMIENTO_CAMBIADO' ? <SwapHorizIcon color="info" /> :
                                                                hist.hismaneve === 'DOCUMENTO_ADJUNTADO' ? <AttachFileIcon color="action" /> :
                                                                    hist.hismaneve === 'DOCUMENTO_ELIMINADO' ? <DeleteForeverIcon color="error" /> :
                                                                        hist.hismaneve === 'EMPLEADO_ASIGNADO' ? <PersonAddIcon color="secondary" /> :
                                                                            hist.hismaneve === 'EMPLEADO_DESASIGNADO' ? <PersonRemoveIcon color="error" /> :
                                                                                hist.hismaneve === 'MANTENIMIENTO_ELIMINADO_LOGICO' ? <VisibilityOffIcon color="error" /> :
                                                                                    <HistoryIcon color="disabled" />
                                                    }
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" component="span" fontWeight="bold">
                                                            {hist.hismandes || `Evento: ${hist.hismaneve}`} {/* Descripción es la principal */}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <React.Fragment>
                                                            <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                                                Tipo: <Chip label={hist.hismaneve} size="small" variant="outlined" />
                                                            </Typography>
                                                            <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                                                Fecha: {formatDateTime(hist.credat)} por <Typography component="span" variant="body2" fontWeight="bold">
                                                                    {hist.personanombre} {hist.personaapellido || ''}
                                                                </Typography>
                                                            </Typography>
                                                            {hist.hismandet && (
                                                                <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                                                    Detalles: {JSON.stringify(hist.hismandet, null, 2)} {/* Formatear JSON */}
                                                                </Typography>
                                                            )}
                                                        </React.Fragment>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider component="li" />
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        No hay historial de eventos para este mantenimiento.
                                    </Typography>
                                )}
                            </List>
                        </Paper>
                    )}
                </Box>
            )}

            {/* Modales */}
            <MaintenanceForm
                open={formState.open}
                handleClose={handleCloseMaintenanceForm}
                onSubmit={handleSubmitMaintenance}
                defaultValues={formState.maintenance || {
                    comid: maintenanceDetails.comid,
                    componentenombre: maintenanceDetails.componentenombre,
                    vehiculomatricula: maintenanceDetails.vehiculomatricula,
                }}
            />

            <MaintenanceDocForm
                open={docFormState.open}
                handleClose={handleCloseDocForm}
                onSubmit={handleSubmitDoc}
                defaultValues={{
                    ...docFormState.doc,
                    manid: maintenanceDetails.manid,
                    mantenimientonombre: `Mantenimiento #${maintenanceDetails.manid} (${maintenanceDetails.tipomantenimientonombre})`,
                }}
            />

            <AssignEmployeeForm
                open={assignEmpFormState.open}
                handleClose={handleCloseAssignEmployeeForm}
                onSubmit={handleSubmitAssignEmployee}
                maintenanceId={maintenanceDetails.manid} // Pasar el ID del mantenimiento
            />

            {/* Diálogos de Confirmación */}
            <Dialog open={confirmDeleteMaintenanceDialog.open} onClose={() => setConfirmDeleteMaintenanceDialog({ open: false, maintenance: null })}>
                <DialogTitle>Confirmar eliminación de mantenimiento</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar lógicamente el mantenimiento #{confirmDeleteMaintenanceDialog.maintenance?.manid}
                        (Tipo: "{confirmDeleteMaintenanceDialog.maintenance?.tipomantenimientonombre}")?
                        Esto lo marcará como inactivo y afectará el historial.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteMaintenanceDialog({ open: false, maintenance: null })} color="inherit">Cancelar</Button>
                    <Button onClick={handleConfirmDeleteMaintenance} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteDocDialog.open} onClose={() => setConfirmDeleteDocDialog({ open: false, doc: null })}>
                <DialogTitle>Confirmar eliminación de documento</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el documento "{confirmDeleteDocDialog.doc?.docnom}"?
                        Esta acción es irreversible y eliminará el archivo asociado.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDocDialog({ open: false, doc: null })} color="inherit">Cancelar</Button>
                    <Button onClick={handleConfirmDeleteDoc} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmRemoveEmployeeDialog.open} onClose={() => setConfirmRemoveEmployeeDialog({ open: false, employeeAssignment: null })}>
                <DialogTitle>Confirmar desasignación de empleado</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea desasignar a "{confirmRemoveEmployeeDialog.employeeAssignment?.empnom} {confirmRemoveEmployeeDialog.employeeAssignment?.empapepat}"
                        de este mantenimiento? Esta acción se registrará en el historial.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRemoveEmployeeDialog({ open: false, employeeAssignment: null })} color="inherit">Cancelar</Button>
                    <Button onClick={handleConfirmRemoveEmployee} color="error" variant="contained">Desasignar</Button>
                </DialogActions>
            </Dialog>

            {/* Modal para visualizar el documento */}
            <Dialog open={openPreview} onClose={handleClosePreview} fullWidth maxWidth="md">
                <DialogTitle>Vista Previa del Documento</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {previewUrl ? (
                        previewUrl.toLowerCase().endsWith(".pdf") ? (
                            <iframe
                                src={previewUrl}
                                width="100%"
                                height="500px" // Altura fija para el iframe
                                style={{ border: "none" }}
                                title="Vista previa del documento"
                            />
                        ) : previewUrl.toLowerCase().match(/\.(jpeg|jpg|gif|png|webp)$/) ? ( // Regex para imágenes
                            <img
                                src={previewUrl}
                                alt="Documento adjunto"
                                style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }}
                            />
                        ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1">Tipo de archivo no soportado para previsualización.</Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => window.open(previewUrl, '_blank')}
                                    startIcon={<DownloadIcon />}
                                    sx={{ mt: 2 }}
                                >
                                    Abrir en nueva pestaña
                                </Button>
                            </Box>
                        )
                    ) : (
                        <Typography variant="body1" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                            No hay documento disponible para previsualizar.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview} color="secondary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar de Alertas */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })}>{alert.message}</Alert>
            </Snackbar>
        </Paper>
    );
};

export default MaintenanceDetailsPage;